import { CheckoutService } from 'app/services/checkout/checkout.service';
import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { ValidateTravellerBudgetRequest, ValidateTravellerBudgetResponse } from 'app/model/checkout/validate-budget.model';
import { FeatureType, PurposeType } from 'app/model/constant';
import { FleetCardInformationResponse } from 'app/model/fleet/fleet.model';
import { environment } from 'environments/environment';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { Checkout3Config } from '../../../../model/checkout/checkout3-config.model';
import { CompanyTaxInvoice } from '../../../../model/checkout/company-tax-invoice.model';
import { AddressesModel, AddressesUserProfileModel } from '../../../../model/profile/profile.model';
import { FeatureModel } from '../../../../model/user.model';
import { ActHttpService, HeaderTypes } from '../../../../services/act-http/act-http.service';
import { AuthenticationService } from '../../../../services/authentication/authentication.service';
import { UserService } from '../../../../services/user/user.service';
import { CartInfoModel } from './../../../../model/checkout/cart-info-model';
import { CartAndProfileModel } from './../../../../model/checkout/cart-profile.model';
import { PaymentChannelCheckout, UploadBookingDocumentResponse } from './../../../../model/checkout/checkout3-config.model';

@Injectable()
export class Checkout3Service {
  private _costCenterFromDropDown = new Map<string, AddressesUserProfileModel>();
  private $updateCostCenterFromDropDown = new BehaviorSubject<Map<string, AddressesUserProfileModel>>(undefined);

  constructor(
    private userService: UserService,
    private actHttp: ActHttpService,
    private authService: AuthenticationService,
    private checkoutService: CheckoutService,
  ) { }

  allowCreditTerm(creditTerm: Boolean, purpose: String): boolean {
    return (creditTerm === true && purpose === PurposeType.work) ? true : false;
  }

  fillterCostCenterFollowMainCompany(costCenterList: AddressesModel[], mainCompanyId: number): AddressesModel[] {
    const newCostCenterList: AddressesModel[] = [];
    costCenterList.forEach(costCenter => costCenter.companyId === mainCompanyId ? newCostCenterList.push(costCenter) : null);
    return newCostCenterList;
  }

  getCompanyTaxAddress(): Observable<CompanyTaxInvoice> {
    const user = this.userService.getUser();
    const requestOption = this.authService.getRequestOptions(HeaderTypes.ACT_TOKEN);
    return this.actHttp.get(environment.endpoint.getTaxInvoice + '/' + user.companyId, requestOption)
      .map((response: Response) => response.json().data);
  }

  getPayableMethod(
    checkout3Config: Checkout3Config,
    purpose: String,
    cartAndProfile: CartAndProfileModel,
    fleetCardListCorporate: FleetCardInformationResponse[],
    fleetCardListPersonal: FleetCardInformationResponse[],
    isShareBudget = false) {

    const isLimitBudget = cartAndProfile.cart.budgetAvailable !== null || cartAndProfile.cart.averageBudget !== null;
    const grandTotalPricePerNight = this.getTotalPricePerNight(cartAndProfile.cart);
    const grandTotalPrice = this.getGrandTotalPrice(cartAndProfile.cart);
    const totalCondition = this.getTotalCondition(cartAndProfile.cart);

    let isOverBudget = false;
    let usedBudgetFleet = false;

    const isAllowMultiPaymentCreditTerms = purpose === PurposeType.work && checkout3Config.allowCreditTerms && checkout3Config.allowMultiPayment;
    const isAllowMultiPaymentCorporateFleet = purpose === PurposeType.work && checkout3Config.allowMultiPaymentCorporateFleet && fleetCardListCorporate && fleetCardListCorporate.length > 0;
    const isAllowBookForTraveller = checkout3Config.allowBookForTraveller && checkout3Config.allowBookForTraveller.allow;
    const budgetAvailable = cartAndProfile.cart.budgetAvailable !== 0;
    const hasPersonalFleet = fleetCardListPersonal && fleetCardListPersonal.length > 0;
    const hasBudgetPerMonthPerYear = cartAndProfile.cart.budgetAvailable !== null;

    let budget = cartAndProfile.cart.budgetAvailable !== null ? cartAndProfile.cart.budgetAvailable : cartAndProfile.cart.averageBudget * totalCondition;
    const isOverBudgetInCondition = this.getIsOverBudgetInCondition(cartAndProfile.cart, cartAndProfile.cart.averageBudget);
    // unlimit budget
    if (budget === undefined) {
      budget = grandTotalPrice;
    }

    if (purpose === PurposeType.work) {
      if (cartAndProfile.cart.shareBudgetCode) {
        isOverBudget = cartAndProfile.cart.budgetAvailable < grandTotalPrice;
      }
      else if (budget >= grandTotalPrice) {
        isOverBudget = false;
      }
      else if (isOverBudgetInCondition) {
        isOverBudget = true;
        // case overbudget and allow book for traveller then display credit terms for user knowing limit budget at dialog alert limit budget and hasn't budget /m/y
        if (isAllowBookForTraveller && !isAllowMultiPaymentCreditTerms && !isAllowMultiPaymentCorporateFleet && budgetAvailable && !hasBudgetPerMonthPerYear) {
          isOverBudget = false;
        }
        if (hasPersonalFleet && !budgetAvailable) {
          usedBudgetFleet = true;
          isOverBudget = fleetCardListPersonal[0].balanceAmount < grandTotalPrice;
        }
      }
      else if (isLimitBudget && checkout3Config.allowLimitBudget.allow && budget < (hasBudgetPerMonthPerYear ? grandTotalPrice : grandTotalPricePerNight)) {
        if (fleetCardListCorporate && fleetCardListCorporate.length > 0 && cartAndProfile.cart.averageBudget) {
          usedBudgetFleet = true;
          budget = Math.min(fleetCardListCorporate[0].balanceAmount, cartAndProfile.cart.averageBudget);
        }
        isOverBudget = budget < (hasBudgetPerMonthPerYear ? grandTotalPrice : grandTotalPricePerNight);

        if (isOverBudget && hasPersonalFleet && !budgetAvailable) {
          usedBudgetFleet = true;
          isOverBudget = fleetCardListPersonal[0].balanceAmount < grandTotalPrice;
        }
      }
    }

    let isCreditTermsAble = false;
    let isMultiPaymentAble = false;
    isCreditTermsAble = purpose === PurposeType.work
      && checkout3Config.allowCreditTerms
      && !isOverBudget
      && !usedBudgetFleet;

    if (isShareBudget) {
      isMultiPaymentAble = purpose === PurposeType.work
        && checkout3Config.allowMultiPayment
        && checkout3Config.allowCreditTerms
    } else {
      isMultiPaymentAble = isAllowMultiPaymentCreditTerms && budgetAvailable
        || isAllowMultiPaymentCorporateFleet
        || hasPersonalFleet;
    }

    let corporateFleet = false;
    corporateFleet = fleetCardListCorporate && fleetCardListCorporate.length > 0 && checkout3Config.allowCorporateFleet;

    const payableMethod = new PaymentChannelCheckout();
    payableMethod.MULTI_PAYMENT.hide = !isMultiPaymentAble;
    payableMethod.CREDIT_TERMS.hide = !isCreditTermsAble;
    payableMethod.CREDIT_CARD.hide = false;
    payableMethod.TRUE_MONEY_WALLET.hide = false;
    payableMethod.FLEET_CARD.hide = false;
    payableMethod.QR_CODE.hide = false;
    payableMethod.E_WALLET.hide = false;
    payableMethod.IM_BANKING.hide = false;
    payableMethod.PAY_AT_COUNTER.hide = false;

    if (isMultiPaymentAble && isOverBudget) {
      payableMethod.MULTI_PAYMENT.default = true;
    } else if (!isCreditTermsAble && corporateFleet) {
      payableMethod.FLEET_CARD.default = true;
    } else if (!isCreditTermsAble) {
      payableMethod.CREDIT_CARD.default = true;
    } else {
      payableMethod.CREDIT_TERMS.default = true;
    }
    const paymentable = payableMethod.getPaymentable();
    paymentable.paymentChannels = paymentable.paymentChannels.filter(item => !item.hide);
    paymentable.mainBookerEmail = cartAndProfile.profile.email.valueOf();
    paymentable.isOverBudget = isOverBudget;
    return paymentable;
  }

  getLimitBudget(): Observable<FeatureModel> {
    const user = this.userService.getUser();
    return this.authService.getUserFeature(null, user, [FeatureType.ALLOW_LIMIT_BUDGET])
      .map(res => res.find(item => item.feature === FeatureType.ALLOW_LIMIT_BUDGET));
  }

  getGrandTotalPrice(cart: CartInfoModel): number {
    let grandTotalPrice = 0;
    cart.optionDetail.optionList.forEach(option => {
      option.conditionList.forEach(condition => {
        if (condition) {
          grandTotalPrice += Math.ceil(condition.totalPriceSellingFinalSupplierCurrency.valueOf() * condition.quantity.valueOf());
        }
      })
    });
    const { validateInfo } = this.checkoutService.getCurrentPromotionCode();
    if (validateInfo && validateInfo.valid) {
      grandTotalPrice = validateInfo.finalAmount;
    }
    return grandTotalPrice;
  }

  getTotalPricePerNight(cart: CartInfoModel): number {
    let grandTotalPrice = 0;
    cart.optionDetail.optionList.forEach(option => {
      option.conditionList.forEach(condition => {
        if (condition) {
          grandTotalPrice += Math.ceil(condition.totalPriceSellingFinalSupplierCurrency.valueOf() / condition.night);
        }
      })
    });
    return grandTotalPrice;
  }

  getTotalCondition(cart: CartInfoModel): number {
    let totalCondition = 0;
    cart.optionDetail.optionList.forEach(option => {
      option.conditionList.forEach(() => {
        totalCondition++;
      })
    });
    return totalCondition;
  }

  getIsOverBudgetInCondition(cart: CartInfoModel, budget): boolean {
    let totalBudgetAvailable = 0;
    cart.optionDetail.optionList.map(option => {
      option.conditionList.map(con => {
        const pricePerNight = con.totalPriceSellingFinalSupplierCurrency.valueOf() / con.night;
        const maximumBudgetPerNight = Math.min(pricePerNight, budget);
        totalBudgetAvailable += maximumBudgetPerNight * con.quantity.valueOf() * con.night;
      })
    });
    return this.getGrandTotalPrice(cart) > totalBudgetAvailable;
  }

  getNight(cart: CartInfoModel) {
    return cart.optionDetail.optionList
      .map(option => option.conditionList[0] ? option.conditionList[0].night : undefined)
      .find(night => !!night);
  }

  getMaximumRoomPricePerNight(cart: CartInfoModel) {
    let roomPricePerNight = 0;
    const night = this.getNight(cart);
    cart.optionDetail.optionList.forEach(option => {
      option.conditionList.forEach(condition => {
        if (condition) {
          const conditionPricePerNight = condition.totalPriceSellingFinalSupplierCurrency.valueOf() / night;
          roomPricePerNight = roomPricePerNight > conditionPricePerNight ? roomPricePerNight : conditionPricePerNight;
        }
      })
    });
    return roomPricePerNight;
  }

  updateCostCenterFromDropDown(bookerEmail: string, val: AddressesUserProfileModel) {
    this._costCenterFromDropDown.set(bookerEmail, val);
    this.$updateCostCenterFromDropDown.next(this._costCenterFromDropDown);
  }

  getCostCenterFromDropDown(bookerEmail: string): Observable<AddressesUserProfileModel> {
    return this.$updateCostCenterFromDropDown.asObservable()
      .filter(res => !!res)
      .map(res => res.get(bookerEmail));
  }

  uploadBookingDocument(file: File): Observable<UploadBookingDocumentResponse> {
    const url = environment.endpoint.uploadBookingDocument;
    const formData = new FormData();
    formData.append('file', file);
    return this.actHttp.post(url, formData, this.actHttp.getRequestOptions(HeaderTypes.ACT_TOKEN, null))
      .map((res) => res.json());
  }

  calculateBudgetAvailable(cartProfile: CartAndProfileModel, isFleetCard?) {
    let isAvailableBudget = true;

    if (cartProfile.cart.budgetAvailable === null) {
      isAvailableBudget = false;
    }

    const budget = isAvailableBudget && !isFleetCard ? cartProfile.cart.budgetAvailable : cartProfile.cart.averageBudget;
    if (isAvailableBudget && !isFleetCard) {
      return cartProfile.cart.budgetAvailable;
    } else {
      return cartProfile.cart.optionDetail.optionList
        .map(option => option.conditionList
          .map(con => {
            if (budget) {
              const perNight = con.totalPriceSellingFinalSupplierCurrency.valueOf() / con.night;
              return Math.min(perNight, budget) * con.quantity.valueOf() * con.night;
            } else {
              return con.totalPriceSellingFinalSupplierCurrency.valueOf() * con.quantity.valueOf();
            }
          }).reduce((next, prev) => next + prev))
        .reduce((next, prev) => next + prev);
    }
  }

  travellerBudgetMultipayment(request: ValidateTravellerBudgetRequest): Observable<ValidateTravellerBudgetResponse[]> {
    return this.actHttp.post(environment.endpoint.travellerBudgetMultipayment, request, this.actHttp.getRequestOptions(HeaderTypes.ACT_TOKEN, null))
      .map((res) => res.json().data);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/checkout-module/checkout3/services/checkout3.service.ts